import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Input,
  Button,
  Divider,
  Modal,
  Loader,
  Timeline,
  ScrollArea,
  Text,
  Table,
  LoadingOverlay,
  Select,
  Tooltip,
} from "@mantine/core";
import { TbDatabaseOff } from "react-icons/tb";

import CustomTable from "./Table";
import { formatTimestampToDate } from "../utils/general";
import { useLazyGetActivityLogsQuery } from "../redux/slices/api.slice";
import { FaArrowDown } from "react-icons/fa6";

type logsType = {
  bat: number;
  date: string;
  deskAlias: string;
  eui: string;
  floorID: string;
  hour: number;
  id: string;
  state: number;
  timestamp: number;
  isManuallyUpdated: boolean
};
const SensorLogs = ({ open, handleClose, sensorID, entityLabel = "desk" }) => {
  const [getSensorLogs, { isLoading, data, isError }] =
    useLazyGetActivityLogsQuery();

    const [mode, setMode] = useState<"half-day" | "day" | "week">("half-day");

  const [token, setToken] = useState(null);
  const [logs, setLogs] = useState<logsType[] | []>([]);
  const observer = useRef<IntersectionObserver | null>(null);
  const endTime = useMemo(() => {
    return new Date();
  }, []);
  const startTime = useMemo(() => {
    let multiplier;
    switch (mode) {
      case "half-day":
        multiplier = 0.5;
        break;
      case "day":
        multiplier = 1;
        break;
      case "week":
        multiplier = 14;
        break;
      default:
        multiplier = 0.5;
    }
    return new Date(endTime.getTime() - multiplier * 24 * 60 * 60 * 1000);
  }, [endTime, mode]);

  const limit = 10;
  const lastRowRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (token) {
            getSensorLogs({
              timeEnd: endTime.getTime(),
              timeStart: startTime.getTime(),
              limit,
              eui: sensorID,
              paginationToken: token,
            });
          }
        }
      });

      if (node) observer.current.observe(node);
    },
    [endTime, startTime, sensorID, limit, token],
  );
  useEffect(() => {
    getSensorLogs({
      timeEnd: endTime.getTime(),
      timeStart: startTime.getTime(),
      limit,
      eui: sensorID,
    });
  }, [startTime]);
  useEffect(() => {
    if (data) {
      setLogs((prev) => {
        const allLogs = [...prev, ...data.Data];
        const uniqueLogs = Array.from(
          new Set(allLogs.map((log) => JSON.stringify(log))),
        ).map((log) => JSON.parse(log));
        return uniqueLogs;
      });

      setToken(data.PaginationToken);
    }
  }, [data]);
  useEffect(() => {
    if (isError) {
      setLogs([]);
    }
  }, [isError]);

  const columnsLabels = [entityLabel, "floorID", "state", "timestamp"];
  const columns = columnsLabels.map((column, index) => {
    return (
      <Table.Th
        key={index}
        className={`  capitalize ${
          index === 0
            ? "rounded-ss-[10px]"
            : index === columnsLabels.length - 1
              ? "rounded-se-[10px]"
              : ""
        }`}
      >
        {column}
      </Table.Th>
    );
  });
  const rows = logs.map((item: logsType, index) => {
    const rowContent = (
      <Table.Tr
        key={item.id}
        ref={index === logs.length - 1 ? lastRowRef : undefined}
        style={{
          backgroundColor: item.isManuallyUpdated ? "#228be6" : undefined,
          color: item.isManuallyUpdated ? "#fff" : undefined,
        }}
      >
        <Table.Td>{item.deskAlias}</Table.Td>
        <Table.Td>{item.floorID}</Table.Td>
        <Table.Td>{item.state}</Table.Td>
        <Table.Td>{formatTimestampToDate(item.timestamp)}</Table.Td>
      </Table.Tr>
    );
  
    if (item.isManuallyUpdated) {
      return (
        <Tooltip key={item.id} label="Manual event change" withArrow>
          {rowContent}
        </Tooltip>
      );
    }
  
    return rowContent;
  });

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      centered
      size="xl"
      // withCloseButton={false}
      radius={20}
    >
      <div className="pb-5 px-4 flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-3xl font-medium text-grayText7 mt-0">
              Sensor Activity Logs
            </h4>
            <p className="text-md mt-3 mb-6 text-grayText5">
              Logs for sensor
              <span className="px-1 text-grayText7 font-md">
                {sensorID}
              </span>{" "}
              over the past {mode === "half-day" ? "12 hours" : mode === "day" ? "24 hours" : "week"}
            </p>
          </div>

          <Select
  data={[
    { label: "12 Hours", value: "half-day" },
    { label: "24 Hours", value: "day" },
    { label: "Week", value: "week" },
  ]}
  value={mode}
  onChange={(value) => {
    setMode(value as "week" | "day" | "half-day");
    setToken(null);
    setLogs([]);
  }}
/>
        </div>
        <div className="relative border my-4 border-grayText4 border-solid rounded-[10px]">
          <CustomTable
            rows={rows}
            columns={columns}
            isLoading={isLoading}
            isError={isError}
            isSensorLogs={true}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SensorLogs;
